import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, Merge, X, Check, Loader2, AlertTriangle } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";

// ── Merge-Modal ────────────────────────────────────────────────────────────────
function MergeModal({ source, onClose, onDone }) {
  const [targetId, setTargetId] = useState("");
  const [targetName, setTargetName] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Live-Suche via API
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get("/artists/", { params: { search: query, limit: 10 } });
        setResults((res.data.items || []).filter(a => a.id !== source.id));
      } catch {} finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, source.id]);

  const handleMerge = async () => {
    if (!targetId) return;
    if (!window.confirm(
      `"${source.name}" wird in "${targetName}" fusioniert.\n\nAlle Releases, Follows und Verknüpfungen werden umgeschrieben.\nDieser Vorgang kann NICHT rückgängig gemacht werden!\n\nFortfahren?`
    )) return;
    setSaving(true);
    try {
      const res = await api.post(`/artists/${source.id}/merge-into/${targetId}`);
      setResult(res.data);
    } catch (e) {
      alert("Fehler: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <h2 className="text-white font-bold text-sm">Artist fusionieren</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        {result ? (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm">
              <Check size={16} /> Fusion erfolgreich
            </div>
            <div className="bg-zinc-800 rounded-xl p-4 text-xs space-y-1.5 text-zinc-300">
              <p><span className="text-zinc-500">Quelle:</span> {result.source}</p>
              <p><span className="text-zinc-500">Ziel:</span> {result.target}</p>
              <div className="border-t border-white/5 pt-2 mt-2 space-y-1">
                <p>Releases aktualisiert: <span className="text-white font-bold">{result.changes?.releases_updated}</span></p>
                <p>Follows übertragen: <span className="text-white font-bold">{result.changes?.follows_merged}</span></p>
                <p>User-Verknüpfungen: <span className="text-white font-bold">{result.changes?.users_updated}</span></p>
              </div>
            </div>
            <button onClick={onDone}
              className="w-full py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-xl transition-colors">
              Schließen & Liste aktualisieren
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="bg-amber-950/40 border border-amber-500/20 rounded-xl p-3 flex gap-2 text-xs text-amber-300">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <span>„<strong>{source.name}</strong>" wird gelöscht. Alle Releases werden dem Ziel-Artist zugewiesen.</span>
            </div>

            <div>
              <label className="block text-zinc-400 text-xs mb-1.5">Zusammenführen in …</label>
              <input
                value={query}
                onChange={e => { setQuery(e.target.value); setTargetId(""); setTargetName(""); }}
                placeholder="Artist suchen…"
                className="w-full bg-zinc-800 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-rose-500 transition"
              />
              {searching && <p className="text-zinc-500 text-xs mt-1 px-1">Suche…</p>}
              {results.length > 0 && (
                <div className="mt-1 bg-zinc-800 border border-white/10 rounded-xl overflow-hidden">
                  {results.map(a => (
                    <button key={a.id} onClick={() => { setTargetId(a.id); setTargetName(a.name); setQuery(a.name); setResults([]); }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2
                        ${targetId === a.id ? "bg-rose-600/20 text-white" : "text-zinc-300 hover:bg-zinc-700"}`}>
                      {a.bild_url && <img src={a.bild_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
                      <span>{a.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={onClose}
                className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-xl transition-colors">
                Abbrechen
              </button>
              <button onClick={handleMerge} disabled={!targetId || saving}
                className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Merge size={14} />}
                Fusionieren
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [mergeSource, setMergeSource] = useState(null);

  const fetchPage = useCallback(async (p = 1, q = "") => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (q) params.search = q;
      const res = await api.get("/artists/", { params });
      setArtists(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(p);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const handleDelete = async (id) => {
    if (!window.confirm("Artist wirklich löschen?")) return;
    try { await api.delete(`/artists/${id}`); fetchPage(page, search); }
    catch { alert("Fehler beim Löschen"); }
  };

  return (
    <AdminLayout>
      {mergeSource && (
        <MergeModal
          source={mergeSource}
          onClose={() => setMergeSource(null)}
          onDone={() => { setMergeSource(null); fetchPage(page, search); }}
        />
      )}
      <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-white font-heading">Artists</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{total} Einträge</p>
        </div>
        <Link to="/admin/artists/new"
          className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-md transition-colors">
          <Plus size={16} /> Neuer Artist
        </Link>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); fetchPage(1, search); }} className="flex gap-2 mb-6">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Artist suchen..."
          className="flex-1 bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600" />
        <button type="submit" className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md transition-colors">Suchen</button>
      </form>

      <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full admin-table">
          <thead>
            <tr className="border-b border-white/5">
              <th className="px-4 py-3 text-left">Bild</th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left hidden md:table-cell">Slug</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}><td colSpan={4} className="px-4 py-3"><div className="h-4 skeleton rounded w-full" /></td></tr>
              ))
            ) : artists.map((a) => (
              <tr key={a.id} className="hover:bg-zinc-800/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800">
                    {a.bild_url
                      ? <img src={a.bild_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full cover-fallback text-xs">{a.name?.[0]}</div>
                    }
                  </div>
                </td>
                <td className="px-4 py-3">
                  <p className="text-white text-sm font-semibold">{a.name}</p>
                  {a.wohnort && <p className="text-zinc-500 text-xs">{a.wohnort}</p>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className="text-zinc-500 text-xs font-mono">{a.slug}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <a href={`/artist/${a.slug}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-rose-900/20 rounded transition-colors" title="Profil ansehen">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </a>
                    <button onClick={() => setMergeSource(a)}
                      className="p-1.5 text-zinc-500 hover:text-amber-400 hover:bg-amber-900/20 rounded transition-colors" title="Fusionieren">
                      <Merge size={15} />
                    </button>
                    <Link to={`/admin/artists/${a.id}/edit`}
                      className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded transition-colors">
                      <Edit size={15} />
                    </Link>
                    <button onClick={() => handleDelete(a.id)}
                      className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <span className="text-zinc-500 text-xs">Seite {page} von {pages}</span>
            <div className="flex gap-1">
              <button onClick={() => fetchPage(page - 1, search)} disabled={page === 1}
                className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-30 rounded"><ChevronLeft size={16} /></button>
              <button onClick={() => fetchPage(page + 1, search)} disabled={page === pages}
                className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-30 rounded"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}
